import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import api from "../api";

const AuthContext = createContext(null);

// Max token age before forcing revalidation (4 hours)
const MAX_TOKEN_AGE_MS = 4 * 60 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const tokenTs = parseInt(localStorage.getItem("token_ts") || "0", 10);
    const age = Date.now() - tokenTs;

    if (token && age < MAX_TOKEN_AGE_MS) {
      api.get("/auth/me")
        .then((res) => setUser(res.data))
        .catch(() => {
          // Token invalid — clean up completely
          localStorage.removeItem("token");
          localStorage.removeItem("token_ts");
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      // No token or expired — clean slate
      localStorage.removeItem("token");
      localStorage.removeItem("token_ts");
      setUser(null);
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (username, password) => {
    // Clear any stale auth state before login attempt
    localStorage.removeItem("token");
    localStorage.removeItem("token_ts");

    const res = await api.post("/auth/login", { username, password });
    localStorage.setItem("token", res.data.access_token);
    localStorage.setItem("token_ts", String(Date.now()));
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Continue with local cleanup
    }
    localStorage.removeItem("token");
    localStorage.removeItem("token_ts");
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, setUser, login, logout, loading }), [user, login, logout, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
